import React, { useCallback, useEffect, useState } from "react";
import { Copy, Loader2, Mail, ShieldCheck, Trash2, UserPlus, Users } from "lucide-react";
import { toast } from "sonner";
import AdminShell from "../../components/layout/AdminShell";
import { useAuth } from "../../context/AuthContext";
import { AuthApi, TeamApi, errorMessage } from "../../lib/api";
import { formatDate, formatMoney } from "../../lib/format";
import { seatLimitLabel, seatPercent } from "../../lib/plans";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Progress } from "../../components/ui/progress";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { useI18n } from "../../i18n";
import RefundOverrideDialog from "../../components/RefundOverrideDialog";

const Team = () => {
  const { t } = useI18n();
  const { currency, user } = useAuth();
  const [data, setData] = useState({ members: [], seats_used: 0, max_seats: 0, pending_invites: 0 });
  const [invites, setInvites] = useState([]);
  const [form, setForm] = useState({ email: "", name: "", role: "employee" });
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [overrideMember, setOverrideMember] = useState(null);

  const load = useCallback(async () => {
    try {
      const [members, inviteList] = await Promise.all([TeamApi.members(), AuthApi.invites()]);
      setData(members);
      setInvites(inviteList);
    } catch (e) {
      toast.error(errorMessage(e, "Could not load the team"));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const invite = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const created = await AuthApi.createInvite(form);
      const link = `${window.location.origin}/invite/${created.token}`;
      await navigator.clipboard?.writeText(link).catch(() => {});
      toast.success(t("team.inviteCreated"), { description: t("team.inviteCopied") });
      setForm({ email: "", name: "", role: "employee" });
      setOpen(false);
      load();
    } catch (err) {
      toast.error(errorMessage(err, "Could not create the invitation"));
    } finally {
      setBusy(false);
    }
  };

  const changeRole = async (member, role) => {
    try {
      await TeamApi.update(member.id, { role });
      toast.success(t("team.roleChanged", { name: member.name, role }));
      load();
    } catch (e) {
      toast.error(errorMessage(e, "Could not change the role"));
    }
  };

  const toggleStatus = async (member) => {
    try {
      await TeamApi.update(member.id, {
        status: member.status === "active" ? "disabled" : "active",
      });
      toast.success(member.status === "active" ? t("team.accessDisabled") : t("team.accessRestored"));
      load();
    } catch (e) {
      toast.error(errorMessage(e, "Could not update access"));
    }
  };

  const removeMember = async (member) => {
    try {
      await TeamApi.remove(member.id);
      toast.success(t("team.removed", { name: member.name }));
      load();
    } catch (e) {
      toast.error(errorMessage(e, "Could not remove the member"));
    }
  };

  const seatPct = seatPercent(
    (data.seats_used || 0) + (data.pending_invites || 0),
    data.max_seats,
    data.unlimited_seats
  );

  return (
    <AdminShell
      title={t("team.title")}
      subtitle={t("team.sub")}
      actions={
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button size="sm" data-testid="invite-open-button">
              <UserPlus className="mr-2 h-4 w-4" /> {t("team.invitePerson")}
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="invite-dialog">
            <DialogHeader>
              <DialogTitle>{t("team.inviteTitle")}</DialogTitle>
              <DialogDescription>
                {t("team.inviteSub")}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={invite} className="space-y-3">
              <div className="space-y-1.5">
                <Label htmlFor="invite-email">{t("team.inviteEmail")}</Label>
                <Input
                  id="invite-email"
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
                  data-testid="invite-email-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="invite-name">{t("team.inviteName")}</Label>
                <Input
                  id="invite-name"
                  value={form.name}
                  onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                  data-testid="invite-name-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label>{t("common.role")}</Label>
                <Select value={form.role} onValueChange={(v) => setForm((p) => ({ ...p, role: v }))}>
                  <SelectTrigger data-testid="invite-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="employee">{t("common.employeeRole")}</SelectItem>
                    <SelectItem value="admin">{t("common.admin")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <DialogFooter>
                <Button type="submit" disabled={busy} data-testid="invite-submit-button">
                  {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Mail className="mr-2 h-4 w-4" />}
                  {t("team.createInvite")}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      }
    >
      <div className="grid gap-4 lg:grid-cols-3">
        <div className="ll-card p-4">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground">
            <Users className="h-3.5 w-3.5" /> {t("team.seatsInUse")}
          </div>
          <div className="ll-num mt-2 font-display text-2xl font-semibold" data-testid="seats-used">
            {data.seats_used} / {seatLimitLabel(t, data.max_seats, data.unlimited_seats)}
          </div>
          {seatPct !== null ? <Progress value={seatPct} className="mt-2 h-1.5" /> : null}
          <p className="mt-2 text-xs text-muted-foreground">
            {t("team.pendingInvites", { count: data.pending_invites })} · {data.plan} ·{" "}
            {t("team.seatFree")}
          </p>
        </div>
        <div className="ll-card p-4 lg:col-span-2">
          <div className="flex items-center gap-2 text-sm font-medium">
            <ShieldCheck className="h-4 w-4 text-primary" /> {t("team.rolePermissions")}
          </div>
          <div className="mt-3 grid gap-3 text-sm text-muted-foreground sm:grid-cols-3">
            <div className="rounded-lg border p-3">
              <p className="font-medium text-foreground">{t("team.adminPerm")}</p>
              <p className="mt-1">{t("team.adminPermBody")}</p>
            </div>
            <div className="rounded-lg border p-3">
              <p className="font-medium text-foreground">{t("team.employeePerm")}</p>
              <p className="mt-1">{t("team.employeePermBody")}</p>
            </div>
          </div>
        </div>
      </div>

      {invites.length ? (
        <div className="ll-card mt-5 overflow-hidden">
          <div className="border-b px-4 py-3">
            <h2 className="font-display text-sm font-semibold">{t("team.pendingTitle")}</h2>
          </div>
          <Table data-testid="pending-invites-table">
            <TableHeader className="bg-muted/40">
              <TableRow>
                <TableHead>{t("common.email")}</TableHead>
                <TableHead>{t("common.role")}</TableHead>
                <TableHead>{t("common.date")}</TableHead>
                <TableHead className="text-right">{t("common.actions")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {invites.map((i) => (
                <TableRow key={i.id}>
                  <TableCell className="font-medium">{i.email}</TableCell>
                  <TableCell className="text-sm capitalize">{i.role}</TableCell>
                  <TableCell className="text-sm">{formatDate(i.expires_at)}</TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        const link = `${window.location.origin}/invite/${i.token}`;
                        navigator.clipboard?.writeText(link);
                        toast.success("Invite link copied");
                      }}
                      data-testid="copy-invite-link"
                    >
                      <Copy className="mr-2 h-3.5 w-3.5" /> {t("team.copyLink")}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={async () => {
                        try {
                          await AuthApi.resendInvite(i.id);
                          toast.success(t("team.inviteEmailed", { email: i.email }));
                        } catch (e) {
                          toast.error(errorMessage(e, "Could not resend"));
                        }
                      }}
                      data-testid="resend-invite-button"
                    >
                      <Mail className="mr-2 h-3.5 w-3.5" /> {t("common.resend")}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-destructive hover:text-destructive"
                      onClick={async () => {
                        await AuthApi.revokeInvite(i.id);
                        load();
                      }}
                      data-testid="revoke-invite-button"
                    >
                      {t("common.revoke")}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : null}

      <div className="ll-card mt-5 overflow-hidden">
        <div className="border-b px-4 py-3">
          <h2 className="font-display text-sm font-semibold">{t("team.membersTitle")}</h2>
        </div>
        <div className="ll-scroll overflow-x-auto">
          <Table data-testid="team-members-table">
            <TableHeader className="bg-muted/40">
              <TableRow>
                <TableHead>{t("common.name")}</TableHead>
                <TableHead>{t("common.email")}</TableHead>
                <TableHead>{t("common.role")}</TableHead>
                <TableHead className="text-right">{t("common.receipts")}</TableHead>
                <TableHead className="text-right">{t("team.colExpenses")}</TableHead>
                <TableHead className="text-right">{t("common.refund")}</TableHead>
                <TableHead>{t("team.colOverride")}</TableHead>
                <TableHead>{t("team.colAccess")}</TableHead>
                <TableHead className="text-right">{t("team.colRemove")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.members.map((m) => (
                <TableRow key={m.id} data-testid="team-member-row">
                  <TableCell className="font-medium">
                    {m.name}
                    {m.id === user?.id ? (
                      <span className="ml-2 rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                        {t("common.you")}
                      </span>
                    ) : null}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">{m.email}</TableCell>
                  <TableCell>
                    <Select
                      value={m.role}
                      onValueChange={(v) => changeRole(m, v)}
                      disabled={m.id === user?.id}
                    >
                      <SelectTrigger className="h-8 w-[130px]" data-testid="member-role-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="employee">{t("common.employeeRole")}</SelectItem>
                        <SelectItem value="admin">{t("common.admin")}</SelectItem>
                          </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell className="ll-num text-right">{m.stats?.receipts ?? 0}</TableCell>
                  <TableCell className="ll-num text-right">
                    {formatMoney(m.stats?.gross, currency)}
                  </TableCell>
                  <TableCell className="ll-num text-right text-primary">
                    {formatMoney(m.stats?.refund, currency)}
                  </TableCell>
                  <TableCell>
                    {m.role === "accountant" ? (
                      <span className="text-xs text-muted-foreground">—</span>
                    ) : (
                      <Button
                        variant={m.refund_override?.enabled ? "secondary" : "ghost"}
                        size="sm"
                        onClick={() => {
                          setOverrideMember(m);
                          setOverrideOpen(true);
                        }}
                        data-testid="member-override-button"
                      >
                        {m.refund_override?.enabled ? t("team.overrideSet") : t("team.overrideNone")}
                      </Button>
                    )}
                  </TableCell>
                  <TableCell>
                    <Button
                      variant={m.status === "active" ? "outline" : "secondary"}
                      size="sm"
                      disabled={m.id === user?.id}
                      onClick={() => toggleStatus(m)}
                      data-testid="member-toggle-status"
                    >
                      {m.status === "active" ? t("common.active") : t("common.disabled")}
                    </Button>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-destructive hover:text-destructive"
                      disabled={m.id === user?.id}
                      onClick={() => removeMember(m)}
                      data-testid="member-remove-button"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
      <RefundOverrideDialog
        member={overrideMember}
        open={overrideOpen}
        onOpenChange={setOverrideOpen}
        currency={currency}
        onSaved={load}
      />
    </AdminShell>
  );
};

export default Team;
